import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Calculator,
  TrendingDown,
  TrendingUp,
  Users,
  Coins,
  Sparkles,
  ArrowRight,
  Mail,
  AlertTriangle,
  CheckCircle2,
  Loader2,
} from 'lucide-react';
import Layout from '../../components/layout/layout';
import Section from '../../components/ui/section';
import SectionBackground from '../../components/sections/section-background';
import { supabase } from '../../lib/supabase';
import {
  WebPageSchema,
  BreadcrumbSchema,
  FAQPageSchema,
} from '../../lib/utils/schema-utils';

// — Typography aligned —
const gradientText =
  'text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]';

const eyebrowClass =
  'inline-flex items-center rounded-full bg-white px-5 py-2 text-sm font-semibold text-neutral-500 uppercase tracking-widest shadow-sm ring-1 ring-neutral-200 mb-6';

const h2Class =
  'text-4xl md:text-5xl font-bold tracking-tight text-[#30475e] mb-5 leading-[1.08]';

const sectionSubtitleClass =
  'text-lg md:text-xl text-neutral-600 leading-relaxed';

// — Hypothèses de calcul (sourcées + ajustables) —
// Coût total de remplacement d'un cadre selon source SHRM / APEC : 1× à 3×
// salaire annuel (incluant : fee recrutement, perte productivité, onboarding,
// désengagement équipe). On prend 1.5× comme valeur prudente.
const REPLACEMENT_COST_MULTIPLIER = 1.5;

// Réduction moyenne du turnover 90 jours observée chez les clients qui ont
// activé le Parcours Découverte (échantillon recruteurs.io 2023-2025) : -40 %.
// Valeur conservatrice pour ne pas survendre.
const TURNOVER_REDUCTION = 0.4;

// Coût annuel moyen d'un Parcours Découverte abonnement (estimation milieu
// de gamme : 1500€/mois × 12 = 18 000€). Utilisé pour calculer le ROI net.
const PARCOURS_ANNUAL_COST = 18000;

interface FormState {
  nbRecrutements: number;
  salaireBrut: number;
  turnoverRate: number;
}

interface LeadState {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
}

const FAQ_ITEMS = [
  {
    q: "Sur quoi se base le calcul du coût d'un mauvais recrutement ?",
    a: "Sources combinées APEC, SHRM et études marché 2023-2025. Le coût total inclut : fee de recrutement (15-25% du salaire), perte de productivité pendant la vacance et l'onboarding (3-6 mois à 100% du salaire), désengagement de l'équipe, coût caché managérial. Total moyen : 1× à 3× le salaire annuel brut. Notre simulateur utilise 1.5× comme valeur prudente.",
  },
  {
    q: "Le -40 % de turnover, vous le mesurez comment ?",
    a: "Cohorte clients recruteurs.io 2023-2025 ayant activé le Parcours Découverte vs cohorte avant activation (même client, même type de poste). Mesure de la rupture < 90 jours et de la démission spontanée < 12 mois. La réduction observée moyenne est de -40 % à -55 % selon les secteurs. On affiche 40 % par prudence.",
  },
  {
    q: "Le coût annuel de 18 000€ est fixe ?",
    a: "C'est la valeur médiane d'un abonnement Parcours Découverte pour une PME qui recrute ~10 cadres/an. Le tarif réel dépend du volume et du périmètre : à partir de 1 500€ pour une mission ponctuelle, jusqu'à 30 000€/an pour un vivier campagne continue. Le simulateur utilise 18 000€ comme moyenne représentative.",
  },
  {
    q: "Que se passe-t-il après le calcul ?",
    a: "Vous recevez les chiffres immédiatement à l'écran. Si vous laissez votre email, on vous envoie une analyse PDF détaillée incluant les hypothèses, les sources, et un benchmark vs votre secteur. Aucun spam, pas d'engagement.",
  },
];

export default function CalculateurRoiPage() {
  const navigate = useNavigate();

  const [form, setForm] = useState<FormState>({
    nbRecrutements: 10,
    salaireBrut: 50000,
    turnoverRate: 20,
  });

  // Calculs dérivés (recalculés à chaque changement de form)
  const computed = useMemo(() => {
    const { nbRecrutements, salaireBrut, turnoverRate } = form;
    const turnoverDecimal = turnoverRate / 100;

    // Coût actuel = nb_recrutements × % qui partent × coût de remplacement (1.5× salaire)
    const currentCost = Math.round(
      nbRecrutements * turnoverDecimal * salaireBrut * REPLACEMENT_COST_MULTIPLIER,
    );

    // Coût avec Parcours Découverte = même formule mais turnover -40 %
    const newTurnoverDecimal = turnoverDecimal * (1 - TURNOVER_REDUCTION);
    const newCost = Math.round(
      nbRecrutements * newTurnoverDecimal * salaireBrut * REPLACEMENT_COST_MULTIPLIER,
    );

    // Économie brute
    const grossSavings = currentCost - newCost;

    // ROI net = économie - coût du Parcours
    const netSavings = grossSavings - PARCOURS_ANNUAL_COST;
    const roiRatio = grossSavings > 0 ? grossSavings / PARCOURS_ANNUAL_COST : 0;

    return {
      currentCost,
      newCost,
      grossSavings,
      netSavings,
      roiRatio,
      currentNbTurnover: Math.round(nbRecrutements * turnoverDecimal),
      newNbTurnover: Math.round(nbRecrutements * newTurnoverDecimal),
    };
  }, [form]);

  // Lead capture state
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [lead, setLead] = useState<LeadState>({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  function setField<K extends keyof FormState>(k: K, v: number) {
    setForm((p) => ({ ...p, [k]: v }));
  }

  function setLeadField<K extends keyof LeadState>(k: K, v: string) {
    setLead((p) => ({ ...p, [k]: v }));
  }

  async function handleLeadSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    const { firstName, lastName, email, company } = lead;
    if (!firstName.trim() || !lastName.trim() || !email.trim() || !company.trim()) {
      setSubmitError('Tous les champs sont requis.');
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      setSubmitError('Format email invalide.');
      return;
    }

    setSubmitting(true);
    try {
      // 1) Insert dans la table leads
      const { error } = await supabase.from('leads').insert([
        {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email: email.trim().toLowerCase(),
          company: company.trim(),
          source: 'calculateur_roi_parcours_decouverte',
          // On stocke les inputs calculés dans le champ "score" (libre)
          score: JSON.stringify({
            inputs: form,
            results: computed,
          }),
        },
      ]);
      if (error) {
        console.error('[calculateur-roi] insert lead error:', error.message);
        setSubmitError(
          "Impossible d'enregistrer votre demande. Réessayez ou contactez contact@recruteurs.io.",
        );
        return;
      }

      // 2) Notification email à Gerald (fire-and-forget)
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
      const anon = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;
      if (supabaseUrl && anon) {
        fetch(
          `${supabaseUrl.replace(/\/$/, '')}/functions/v1/whitepaper-notify`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${anon}`,
              apikey: anon,
            },
            body: JSON.stringify({
              paper: {
                slug: 'calculateur-roi-parcours-decouverte',
                title:
                  'Calculateur ROI Parcours Découverte — résultats personnalisés',
              },
              lead: {
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                email: email.trim().toLowerCase(),
                company: company.trim(),
              },
              metadata: {
                submittedAt: new Date().toISOString(),
                referrer:
                  typeof window !== 'undefined' ? window.location.pathname : '',
                userAgent:
                  typeof navigator !== 'undefined' ? navigator.userAgent : '',
              },
            }),
            keepalive: true,
          },
        ).catch((err) =>
          console.warn('[calculateur-roi] notify failed:', err),
        );
      }

      setSubmitted(true);
    } catch (err) {
      console.error('[calculateur-roi] submit error:', err);
      setSubmitError('Une erreur est survenue. Réessayez.');
    } finally {
      setSubmitting(false);
    }
  }

  function formatEUR(n: number): string {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(n);
  }

  return (
    <Layout
      title="Calculateur ROI · Coût d'un mauvais recrutement vs Parcours Découverte | recruteurs.io"
      description="Combien vous coûte chaque année un mauvais recrutement ? Notre calculateur estime votre coût annuel du turnover et l'économie potentielle avec la Plateforme Parcours Découverte. Sources APEC, SHRM. Gratuit."
      keywords="calculateur coût recrutement, ROI parcours découverte, coût turnover, coût mauvais recrutement, simulation recrutement, recruteurs.io"
    >
      <WebPageSchema
        page={{
          title:
            "Calculateur ROI · Coût d'un mauvais recrutement vs Parcours Découverte",
          description:
            "Estimez en 30 secondes votre coût annuel du turnover et l'économie potentielle avec la Plateforme Parcours Découverte. Sources APEC, SHRM.",
          url: 'https://recruteurs.io/ressources/calculateur-roi',
          lastReviewed: '2026-05-15',
        }}
      />

      <BreadcrumbSchema
        items={[
          { name: 'Accueil', url: 'https://recruteurs.io' },
          {
            name: 'Ressources',
            url: 'https://recruteurs.io/ressources/articles',
          },
          {
            name: 'Calculateur ROI',
            url: 'https://recruteurs.io/ressources/calculateur-roi',
          },
        ]}
      />

      <FAQPageSchema
        questions={FAQ_ITEMS.map((it) => ({ question: it.q, answer: it.a }))}
      />

      {/* HERO compact (pas de HeroTemplateEnts — on veut le calculateur en haut) */}
      <SectionBackground
        page="calculateur"
        section="calc-hero"
        className="relative overflow-hidden bg-gradient-to-br from-[#fff1f2] via-[#fef9c3] to-[#e0f2ff]"
      >
        <div
          aria-hidden
          className="absolute inset-0 z-0 pointer-events-none"
        >
          <div className="absolute -top-32 -left-32 w-96 h-96 bg-[#d03484]/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-[#d47254]/10 rounded-full blur-3xl" />
        </div>

        <section className="relative z-10 py-14 md:py-16">
          <div className="container-custom max-w-3xl mx-auto text-center">
            <span className={eyebrowClass}>
              <Calculator className="w-4 h-4 mr-2 text-[#d03484]" />
              Calculateur · Gratuit · 30 secondes
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.1] pb-1 mb-5">
              <span className="text-[#30475e]">Combien vous coûte </span>
              <span className={gradientText}>un mauvais recrutement ?</span>
            </h1>
            <p className="text-base md:text-lg text-neutral-700 leading-relaxed">
              Trois inputs, un résultat. Estimez votre coût annuel du turnover
              et l'économie potentielle avec la <strong>Plateforme Parcours
              Découverte</strong>. Sources : APEC, SHRM, échantillon
              recruteurs.io 2023-2025.
            </p>
          </div>
        </section>
      </SectionBackground>

      {/* CALCULATEUR — 2 colonnes : inputs à gauche, résultats à droite */}
      <SectionBackground page="calculateur" section="calc-tool" className="bg-white">
        <Section>
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_1.3fr] gap-6 lg:gap-8">
            {/* INPUTS */}
            <div className="rounded-[1.75rem] bg-white ring-1 ring-neutral-200 shadow-sm p-6 md:p-8 space-y-7">
              <div>
                <h2 className="text-xl font-bold text-[#30475e] mb-1">
                  Vos paramètres
                </h2>
                <p className="text-xs text-neutral-500">
                  Ajustez les curseurs — les résultats se mettent à jour en temps réel.
                </p>
              </div>

              {/* Nb recrutements */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-semibold text-[#30475e]">
                    <Users className="w-4 h-4 inline mr-1.5 text-[#d03484]" />
                    Recrutements cadres / an
                  </label>
                  <span className="text-2xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]">
                    {form.nbRecrutements}
                  </span>
                </div>
                <input
                  type="range"
                  min={1}
                  max={50}
                  value={form.nbRecrutements}
                  onChange={(e) => setField('nbRecrutements', parseInt(e.target.value, 10))}
                  className="w-full accent-[#d03484]"
                />
                <div className="flex justify-between text-[10px] text-neutral-400 mt-1">
                  <span>1</span>
                  <span>25</span>
                  <span>50+</span>
                </div>
              </div>

              {/* Salaire brut annuel moyen */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-semibold text-[#30475e]">
                    <Coins className="w-4 h-4 inline mr-1.5 text-[#d03484]" />
                    Salaire brut annuel moyen
                  </label>
                  <span className="text-2xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]">
                    {formatEUR(form.salaireBrut)}
                  </span>
                </div>
                <input
                  type="range"
                  min={30000}
                  max={150000}
                  step={5000}
                  value={form.salaireBrut}
                  onChange={(e) => setField('salaireBrut', parseInt(e.target.value, 10))}
                  className="w-full accent-[#d03484]"
                />
                <div className="flex justify-between text-[10px] text-neutral-400 mt-1">
                  <span>30 k€</span>
                  <span>80 k€</span>
                  <span>150 k€</span>
                </div>
              </div>

              {/* Turnover actuel */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-semibold text-[#30475e]">
                    <TrendingDown className="w-4 h-4 inline mr-1.5 text-[#d03484]" />
                    Turnover actuel sous 12 mois
                  </label>
                  <span className="text-2xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]">
                    {form.turnoverRate} %
                  </span>
                </div>
                <input
                  type="range"
                  min={5}
                  max={60}
                  value={form.turnoverRate}
                  onChange={(e) => setField('turnoverRate', parseInt(e.target.value, 10))}
                  className="w-full accent-[#d03484]"
                />
                <div className="flex justify-between text-[10px] text-neutral-400 mt-1">
                  <span>5 %</span>
                  <span>30 %</span>
                  <span>60 %</span>
                </div>
                <p className="text-[10px] text-neutral-400 mt-1.5">
                  Moyenne marché cadres France : 15-25 %. Source : APEC 2024.
                </p>
              </div>

              {/* Hypothèses */}
              <details className="rounded-xl bg-[#fbfaf8] ring-1 ring-neutral-200 px-4 py-3 text-xs">
                <summary className="font-semibold text-neutral-500 cursor-pointer">
                  Hypothèses de calcul
                </summary>
                <ul className="mt-2 space-y-1 text-neutral-500">
                  <li>
                    • Coût total de remplacement = <strong>1.5×</strong> salaire annuel
                    (APEC / SHRM : 1×–3×)
                  </li>
                  <li>
                    • Réduction turnover avec Parcours Découverte = <strong>-40 %</strong>{' '}
                    (échantillon recruteurs.io 2023-2025)
                  </li>
                  <li>
                    • Coût annuel Parcours Découverte = <strong>{formatEUR(PARCOURS_ANNUAL_COST)}</strong>{' '}
                    (médiane abonnement vivier)
                  </li>
                </ul>
              </details>
            </div>

            {/* RESULTATS */}
            <div className="space-y-4">
              {/* Card principale : économie nette */}
              <motion.div
                key={computed.netSavings}
                initial={{ scale: 0.98, opacity: 0.7 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.25 }}
                className="rounded-[1.75rem] bg-gradient-to-br from-[#30475e] to-[#1f3245] text-white p-7 md:p-9 shadow-2xl"
              >
                <p className="text-[11px] font-bold uppercase tracking-widest text-[#fde7f2] mb-2">
                  Économie nette annuelle estimée
                </p>
                <p className="text-4xl md:text-5xl font-extrabold leading-none mb-3">
                  <span
                    className={
                      computed.netSavings >= 0
                        ? 'text-transparent bg-clip-text bg-gradient-to-r from-[#fde7f2] to-[#fde2d3]'
                        : 'text-amber-300'
                    }
                  >
                    {computed.netSavings >= 0 ? '+' : ''}
                    {formatEUR(computed.netSavings)}
                  </span>
                </p>
                <p className="text-sm text-white/80 leading-relaxed">
                  = économie sur turnover (
                  <strong>{formatEUR(computed.grossSavings)}</strong>) moins
                  le coût annuel Parcours Découverte (
                  <strong>{formatEUR(PARCOURS_ANNUAL_COST)}</strong>).
                </p>
                {computed.roiRatio >= 1 && (
                  <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur-sm px-3 py-1 text-xs font-semibold">
                    <TrendingUp className="w-3.5 h-3.5" />
                    ROI : {computed.roiRatio.toFixed(1)}× le coût investi
                  </div>
                )}
              </motion.div>

              {/* Détail comparatif */}
              <div className="grid grid-cols-2 gap-4">
                {/* Coût actuel */}
                <div className="rounded-2xl bg-white ring-1 ring-neutral-200 shadow-sm p-5">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-500 mb-1">
                    Aujourd'hui
                  </p>
                  <p className="text-2xl font-extrabold text-[#30475e] mb-1 leading-none">
                    {formatEUR(computed.currentCost)}
                  </p>
                  <p className="text-xs text-neutral-500 leading-snug">
                    Coût annuel du turnover<br />
                    ({computed.currentNbTurnover} départs / an)
                  </p>
                </div>
                {/* Coût avec Parcours */}
                <div className="rounded-2xl bg-gradient-to-br from-[#fde7f2] to-[#fde2d3] ring-1 ring-[#d03484]/20 shadow-sm p-5">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-[#d03484] mb-1">
                    Avec Parcours Découverte
                  </p>
                  <p className="text-2xl font-extrabold text-[#30475e] mb-1 leading-none">
                    {formatEUR(computed.newCost)}
                  </p>
                  <p className="text-xs text-neutral-600 leading-snug">
                    Coût annuel du turnover<br />
                    ({computed.newNbTurnover} départs / an)
                  </p>
                </div>
              </div>

              {/* CTA lead capture */}
              {!showLeadForm && !submitted && (
                <button
                  type="button"
                  onClick={() => setShowLeadForm(true)}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-6 py-3 text-sm font-bold text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all whitespace-nowrap"
                >
                  Recevoir l'analyse détaillée par email
                  <Mail className="w-4 h-4" />
                </button>
              )}

              {/* Formulaire lead capture */}
              {showLeadForm && !submitted && (
                <motion.form
                  onSubmit={handleLeadSubmit}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25 }}
                  className="rounded-2xl bg-white ring-1 ring-neutral-200 shadow-sm p-5 md:p-6 space-y-3"
                >
                  <p className="text-sm font-bold text-[#30475e]">
                    Recevoir l'analyse PDF détaillée
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="text"
                      value={lead.firstName}
                      onChange={(e) => setLeadField('firstName', e.target.value)}
                      placeholder="Prénom"
                      autoComplete="given-name"
                      disabled={submitting}
                      className="rounded-lg ring-1 ring-neutral-200 focus:ring-2 focus:ring-[#d03484]/40 bg-[#fbfaf8] focus:bg-white px-3 py-2 text-sm outline-none"
                    />
                    <input
                      type="text"
                      value={lead.lastName}
                      onChange={(e) => setLeadField('lastName', e.target.value)}
                      placeholder="Nom"
                      autoComplete="family-name"
                      disabled={submitting}
                      className="rounded-lg ring-1 ring-neutral-200 focus:ring-2 focus:ring-[#d03484]/40 bg-[#fbfaf8] focus:bg-white px-3 py-2 text-sm outline-none"
                    />
                  </div>
                  <input
                    type="email"
                    value={lead.email}
                    onChange={(e) => setLeadField('email', e.target.value)}
                    placeholder="Email professionnel"
                    autoComplete="email"
                    disabled={submitting}
                    className="w-full rounded-lg ring-1 ring-neutral-200 focus:ring-2 focus:ring-[#d03484]/40 bg-[#fbfaf8] focus:bg-white px-3 py-2 text-sm outline-none"
                  />
                  <input
                    type="text"
                    value={lead.company}
                    onChange={(e) => setLeadField('company', e.target.value)}
                    placeholder="Société"
                    autoComplete="organization"
                    disabled={submitting}
                    className="w-full rounded-lg ring-1 ring-neutral-200 focus:ring-2 focus:ring-[#d03484]/40 bg-[#fbfaf8] focus:bg-white px-3 py-2 text-sm outline-none"
                  />
                  {submitError && (
                    <div className="rounded-lg bg-red-50 ring-1 ring-red-200 px-3 py-2 text-xs text-red-700 flex items-start gap-2">
                      <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                      <span>{submitError}</span>
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-5 py-2.5 text-sm font-bold text-white shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all disabled:opacity-60 whitespace-nowrap"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Envoi...
                      </>
                    ) : (
                      <>
                        Recevoir l'analyse
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                  <p className="text-[10px] text-neutral-400 leading-relaxed">
                    Vous recevez une analyse PDF avec vos hypothèses, le détail
                    par poste et un benchmark sectoriel. Pas de spam.
                  </p>
                </motion.form>
              )}

              {/* Success state */}
              {submitted && (
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="rounded-2xl bg-gradient-to-br from-[#fde7f2] to-[#fde2d3] ring-1 ring-[#d03484]/20 shadow-sm p-5 md:p-6 text-center"
                >
                  <CheckCircle2 className="w-10 h-10 text-[#d03484] mx-auto mb-3" />
                  <p className="text-base font-bold text-[#30475e] mb-1">
                    Demande bien reçue, {lead.firstName} !
                  </p>
                  <p className="text-sm text-neutral-700 leading-relaxed">
                    Vous recevez l'analyse PDF sur <strong>{lead.email}</strong>{' '}
                    sous 24 h ouvrées.
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate('/reservation')}
                    className="mt-4 inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-4 py-2 text-xs font-bold text-white shadow-md hover:shadow-lg transition-all whitespace-nowrap"
                  >
                    Réserver un échange de 15 min
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </motion.div>
              )}
            </div>
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 3 — FAQ méthodologique */}
      <SectionBackground
        page="calculateur"
        section="calc-faq"
        className="bg-[#fbfaf8]"
      >
        <Section>
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <span className={eyebrowClass}>Méthodologie</span>
              <h2 className={h2Class}>
                D'où viennent{' '}
                <span className={gradientText}>les chiffres ?</span>
              </h2>
              <p className={sectionSubtitleClass}>
                Toutes nos hypothèses sont sourcées et conservatrices. La transparence
                de méthode est notre meilleure démonstration.
              </p>
            </div>

            <div className="space-y-3">
              {FAQ_ITEMS.map((item, idx) => (
                <details
                  key={idx}
                  className="group rounded-2xl bg-white ring-1 ring-neutral-200 hover:ring-[#d03484]/30 transition-all"
                >
                  <summary className="flex items-center justify-between gap-3 cursor-pointer px-5 py-4 text-sm md:text-base font-semibold text-[#30475e] hover:text-[#d03484] transition-colors">
                    <span>{item.q}</span>
                    <span className="flex-shrink-0 text-[#d03484] text-xl leading-none group-open:rotate-45 transition-transform">
                      +
                    </span>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-neutral-600 leading-relaxed">
                    {item.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 4 — Lien vers la page pilier */}
      <SectionBackground
        page="calculateur"
        section="calc-bridge"
        className="bg-white"
      >
        <Section>
          <div className="max-w-3xl mx-auto text-center">
            <Sparkles className="w-10 h-10 text-[#d03484] mx-auto mb-5" />
            <h2 className={h2Class}>
              Le calcul a son intérêt.{' '}
              <span className={gradientText}>La méthode aussi.</span>
            </h2>
            <p className={`${sectionSubtitleClass} mb-8 max-w-2xl mx-auto`}>
              Découvrez en détail comment fonctionne la Plateforme Parcours
              Découverte — les 5 étapes, le double dividende, les modèles
              commerciaux, et les +1 000 candidats déjà accueillis.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => navigate('/decouvrir')}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-6 py-2.5 text-sm font-bold text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all whitespace-nowrap"
              >
                Voir la Plateforme Parcours Découverte
                <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => navigate('/pourquoi-recruteurs-io')}
                className="inline-flex items-center justify-center gap-2 rounded-2xl ring-1 ring-neutral-200 bg-white px-6 py-2.5 text-sm font-semibold text-[#30475e] hover:bg-[#fbfaf8] transition-all whitespace-nowrap"
              >
                Pourquoi nous vs cabinet classique
              </button>
            </div>
          </div>
        </Section>
      </SectionBackground>
    </Layout>
  );
}
